import { useCallback, useEffect, useMemo, useState } from "react"
import { usePage } from "@inertiajs/react"
import { toast } from "sonner"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { uploadProjectSketch } from "@/lib/library-api"
import { fetchSketchEdit } from "@/lib/sketch-api"
import { useSketchJobResultStore } from "@/stores/sketch-job-result-store"
import type { ProjectSummary } from "@/types/workspace"

export function SketchJobResultHost() {
  const { t } = useTranslation("workspace")
  const page = usePage<{
    project?: ProjectSummary
    sketch?: { available: boolean; modelLabel: string; modelKey?: string }
  }>()
  const project = page.props.project
  const sketch = page.props.sketch
  const target = useSketchJobResultStore((s) => s.target)
  const closeSketchJobResult = useSketchJobResultStore((s) => s.closeSketchJobResult)

  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const sketchEdit = useMemo(
    () => ({
      available: Boolean(sketch?.available),
      modelLabel: sketch?.modelLabel ?? "",
      modelKey: sketch?.modelKey,
    }),
    [sketch],
  )

  useEffect(() => {
    if (!target || !project) {
      setSourceUrl(null)
      setLoading(false)
      return
    }

    const directUrl = (target.downloadUrl ?? target.sourceUrl)?.trim() || null
    if (directUrl) {
      setSourceUrl(directUrl)
      setLoading(false)
      return
    }

    if (target.editId == null) {
      toast.error(t("canvas.sketch.editFailed"))
      closeSketchJobResult()
      return
    }

    let cancelled = false
    setLoading(true)
    setSourceUrl(null)

    void fetchSketchEdit(target.projectId, target.editId)
      .then((result) => {
        if (cancelled) return
        if (result.status !== "completed" || !result.url) {
          toast.error(result.error?.trim() || t("canvas.sketch.editFailed"))
          closeSketchJobResult()
          return
        }
        setSourceUrl(result.url)
      })
      .catch((e) => {
        if (cancelled) return
        toast.error(e instanceof Error ? e.message : t("canvas.sketch.editFailed"))
        closeSketchJobResult()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [target, project, closeSketchJobResult, t])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeSketchJobResult()
    },
    [closeSketchJobResult],
  )

  const handleSaveToLibrary = useCallback(
    async (file: File) => {
      if (!project) return
      await uploadProjectSketch(project.id, file)
      toast.success(t("canvas.sketch.savedToLibrary"))
    },
    [project, t],
  )

  const handleSaveExistingToLibrary = useCallback(async () => {
    toast.success(t("canvas.sketch.savedToLibrary"))
  }, [t])

  if (!project || !target || loading || !sourceUrl) return null

  const presentation = target.presentation ?? "view"
  const persist = presentation === "edit" ? "library" : "none"
  const dialogKey =
    target.editId != null
      ? `job-result:edit:${target.editId}`
      : `job-result:url:${target.sourceUrl ?? target.downloadUrl ?? target.fileName}`

  return (
    <SketchDialog
      key={dialogKey}
      open
      onOpenChange={handleOpenChange}
      projectId={project.id}
      projectFrameFormat={project.frame_format}
      sketchEdit={sketchEdit}
      sourceUrl={sourceUrl}
      initialFileName={target.fileName}
      presentation={presentation}
      persist={persist}
      allowCleanLibrarySave={presentation === "edit"}
      onSaveToLibrary={presentation === "edit" ? handleSaveToLibrary : undefined}
      onSaveExistingToLibrary={presentation === "edit" ? handleSaveExistingToLibrary : undefined}
    />
  )
}
